<template>
  <div
    :class="{
      'fact-finder-item': true,
      'fact-finder-item--fite': isFite,
    }"
  >
    <a
      class="fact-finder-item__tile"
      data-test="search-result-item"
      :href="href"
      @click="onClick"
    >
      <div
        v-if="bestPrice?.price?.savingsInformation"
        class="fact-finder-item__savings"
      >
        {{
          $dict.get('online-Shop.Product.Savings', [
            bestPrice.price.savingsInformation.savingsInPercent,
          ])
        }}
      </div>
      <ssr-cloud-image-fi
        v-if="imageUrl"
        :src="imageUrl"
        :aspect-ratio="1"
        :unlimited="248"
        :breakpoints="breakpoints"
        :alt="itemName"
        class="fact-finder-item__image"
        params="func=fit"
        trim
        do-not-replace-url
      />

      <!-- eslint-disable vue/no-v-html -->
      <div
        class="fact-finder-item__name"
        data-test="search-result-item-name"
        v-html="highlightName"
      />
      <!-- eslint-enable vue/no-v-html -->
      <div
        v-if="isProduct"
        class="fact-finder-item__article-nr"
        data-test="article-number"
      >
        {{ $dict.get('product-List.Table.Article-Number') }}
        {{ variant.ID }}
      </div>

      <div
        v-if="isFamily"
        :class="{
          'fact-finder-item__family': true,
          'fact-finder-item__family--has-description': showDescription,
        }"
      >
        <div
          v-if="showDescription"
          class="fact-finder-item__description"
        >
          {{ record.masterValuesObject.catalogCategoryData.MarketingClaim }}
        </div>

        <div
          v-if="record.variantCount"
          :cat-id="variant.CategoryPathId"
          class="fact-finder-item__family-variants"
          data-test="search-result-item-variants-count"
        >
          {{
            $dict.get(
              record.variantCount === 1 ? 'search.Variants-Singular' : 'search.Variants-Plural',
              [record.variantCount],
            )
          }}
        </div>
      </div>
      <div
        v-else-if="isProduct"
        class="fact-finder-item__product-info"
      >
        <template v-if="isFite">
          <icon-attribute-item-fi
            v-if="attributeValue(variant.Attributes, 'ALTER_AB')"
            class="fact-finder-item__recommended-age"
            icon="age"
            :size="1"
            :value="attributeValue(variant.Attributes, 'ALTER_AB')"
            :label="attributeName(variant.Attributes, 'ALTER_AB')"
          />
          <div
            v-if="variant.ProductPrice"
            class="fact-finder-item__uvp-price"
          >
            <div class="fact-finder-item__uvp-price-value">
              <loading-indicator
                v-if="loading"
                variant="spinnerLocal"
              />
              <template v-if="shopifyPrice">
                {{ shopifyPrice }}
              </template>
              <template v-else>
                {{ `${uvpPricePrefix} ${currentProductPrice}` }}
              </template>
            </div>
            <div class="fact-finder-item__uvp-price-vat">
              {{ $dict.get('online-Shop.Product.Vat-included') }}
            </div>
          </div>
        </template>
        <template v-else>
          <div
            v-if="bestPrice"
            class="fact-finder-item__best-price"
          >
            <div>
              <span
                v-if="bestPrice.price.savingsInformation"
                class="fact-finder-item__stroke-price"
              >
                {{ bestPrice.price.savingsInformation.totalStrokePriceValueFormatted }}
              </span>
              <span class="fact-finder-item__best-price-value">
                {{ bestPrice.price.valueFormatted }}
              </span>
            </div>
            <div class="fact-finder-item__best-price-vat">
              {{ $dict.get('online-Shop.Product.Vat-excluded') }}
            </div>
          </div>
        </template>
      </div>
      <div
        v-else-if="isPage"
        class="fact-finder-item__page"
      >
        <!-- eslint-disable vue/no-v-html -->
        <div
          class="fact-finder-item__texts"
          v-html="highlightText"
        />
        <!-- eslint-enable vue/no-v-html -->
        <div
          v-if="record.variantValuesObject[0].CategoryPathName?.[0]"
          class="fact-finder-item__path"
        >
          {{
            $dict.get('search.Found-In', [
              record.variantValuesObject[0].CategoryPathName[0].join(' | '),
            ])
          }}
        </div>
        <arrow-icon class="fact-finder-item__arrow" />
      </div>
    </a>
  </div>
</template>

<script>
import { ArrowIcon, IconAttributeItemFi, LoadingIndicator, SsrCloudImageFi } from 'atoms';

import { usePriceCurrency } from '@/composables/priceCurrency';
import catalogAttributesMixin from '@/mixins/catalogAttributesMixin.vue';
import catalogMixin from '@/mixins/catalogMixin.vue';
import eventHubMixin from '@/mixins/eventHubMixin.vue';

const shortenText = (text) =>
  text.replace(/^[^<]+</, (lead) => {
    const shortLead = / [^ ]{0,15}.{15}$/.exec(lead);
    return shortLead ? `...${shortLead[0]}` : lead;
  });

const escapeChars = (unescaped) =>
  (unescaped || '').replace(/[<>&]/g, (char) => `&#${char.charCodeAt(0)};`);

export default {
  name: 'FactFinderItem',
  components: {
    ArrowIcon,
    IconAttributeItemFi,
    SsrCloudImageFi,
    LoadingIndicator,
  },
  mixins: [catalogMixin, catalogAttributesMixin, eventHubMixin],
  inject: ['getSearchData'],
  props: {
    record: {
      type: Object,
      required: true,
    },
    bestPrice: {
      type: Object,
      default: null,
    },
    filterUrl: {
      type: String,
      default: '',
    },
    showDescription: {
      type: Boolean,
      default: false,
    },
    type: {
      type: String,
      required: true,
    },
    recordIdx: {
      type: Number,
      required: true,
    },
  },
  setup() {
    const { formatPrice } = usePriceCurrency();
    return {
      formatPrice,
      breakpoints: [
        {
          mediaWidth: 369,
          imageWidth: 285,
        },
        {
          mediaWidth: 479,
          imageWidth: 395,
        },
        {
          mediaWidth: 767,
          imageWidth: 318,
        },
      ],
    };
  },
  computed: {
    variant() {
      if (this.record.masterValuesObject?.masterProductData?.catalogProductData) {
        const {
          ProductId,
          ProductName,
          Slug,
          imageUrl,
          ProductPrice,
          ProductPriceCurrency,
          Attributes,
        } = this.record.masterValuesObject.masterProductData.catalogProductData;

        const { bestPrice } = this.record.masterValuesObject.masterProductData;

        return {
          ID: ProductId,
          Name: ProductName,
          ImageURL: imageUrl,
          Slug,
          bestPrice,
          ProductPrice,
          ProductPriceCurrency,
          Attributes,
        };
      }

      const variant = this.record.variantValuesObject?.[0];
      if (!variant) {
        if (this.type !== 'family') {
          console.error('record missing variant', this.record, this.type);
        }
        return {};
      }
      return variant;
    },
    imageUrl() {
      if (this.isFamily) {
        if (this.record.masterValuesObject.catalogCategoryData?.imageUrl) {
          return this.record.masterValuesObject.catalogCategoryData.imageUrl;
        }
      }

      if (this.isProduct) {
        return this.variant.ImageURL;
      }
      return null;
    },
    href() {
      const href = [];
      if (this.isFamily || this.isProduct) {
        href.push(this.$fischer.pages.products().url);
      }
      if (this.isFamily) {
        href.push(this.record.masterValuesObject.catalogCategoryData.Slug, this.filterUrl);
      } else {
        href.push(this.variant.Slug);
      }
      return href.join('');
    },
    highlightText() {
      const markedText = this.markText(this.record.variantValuesObject[0].Texts);
      return shortenText(markedText);
    },
    itemName() {
      if (this.isFamily) {
        return (
          this.record.masterValuesObject.catalogCategoryData?.CategoryName ||
          (this.record.masterValuesObject.catalogProductData &&
            this.record.masterValuesObject.masterProductData.catalogProductData.CategoryName)
        );
      }
      return this.record.Name || this.variant.Name || '';
    },
    highlightName() {
      if (!this.isPage) {
        return escapeChars(this.itemName);
      }

      return this.markText(this.itemName);
    },
    isProduct() {
      return (
        this.type === 'product' ||
        Boolean(this.record.masterValuesObject?.masterProductData?.catalogProductData)
      );
    },
    isFamily() {
      return this.type === 'family' && !this.isProduct;
    },
    isPage() {
      return this.type === 'page';
    },
    uvpPricePrefix() {
      return this.$dict.get('online-Shop.Product.UVP-Price-Prefix') || '';
    },
    shopifyProducts() {
      return this.$store.getters['shopify/shopifyProducts'];
    },
    loading() {
      return this.$store.getters['shopify/loading'];
    },
    shopifyPrice() {
      const priceInfo = this.findPriceBySku(this.record.idString);
      if (priceInfo && priceInfo.price) {
        return this.formatPrice(priceInfo.price.amount, priceInfo.price.currencyCode);
      }
      return null;
    },
    currentProductPrice() {
      return this.formatPrice(this.variant.ProductPrice, this.variant.ProductPriceCurrency);
    },
  },

  methods: {
    onClick(event) {
      const { pageSize, clickSource, query, attachBreadcrumb } = this.getSearchData();
      let eventQuery;
      if (attachBreadcrumb) {
        const productBreadcrumb = this.$store.getters['navigation/productBreadcrumb'];
        const breadcrumb = productBreadcrumb.map((item) => item.title);
        breadcrumb.unshift(query);
        breadcrumb.push(this.itemName);
        eventQuery = breadcrumb.join('/');
      } else {
        eventQuery = query;
      }
      this.sendClickToEventHub(
        {
          clickSource,
          familyId: this.isFamily ? this.record.idString : undefined,
          page: Math.min(1, Math.ceil(pageSize / this.recordIdx)),
          pageSize,
          pageTitle: this.itemName,
          position: this.recordIdx + 1,
          productId: this.isProduct ? this.variant.ID : undefined,
          productTitle: this.isProduct ? this.itemName : null,
          query: eventQuery,
          score: this.record.score,
        },
        event,
        this.href,
      );
    },
    markText(text = '') {
      const escapedText = escapeChars(text);

      const query = this.$store.getters['search/getQuery'];
      const words = escapeChars(query)
        .split(' ')
        .filter((x) => x.length > 2);
      if (!words.length) {
        return escapedText;
      }

      const regexSafeWords = words.map((word) =>
        // eslint-disable-next-line no-useless-escape
        word.replace(/[\[\\^$.|?*+()]/g, ([char]) => `\\${char}`),
      );
      const wordsRegex = new RegExp(regexSafeWords.join('|'), 'gi');
      const markedText = escapedText.replace(wordsRegex, (word) => `<mark>${word}</mark>`);

      return markedText;
    },
    findPriceBySku(skuPadded) {
      if (!this.shopifyProducts || !this.shopifyProducts.length || !skuPadded) {
        return null;
      }
      const sku = skuPadded.replace(/^0+/, ''); // shopify will remove leading 0s

      for (const productEdge of this.shopifyProducts) {
        const product = productEdge.node;

        if (product.variants && product.variants.edges) {
          for (const variantEdge of product.variants.edges) {
            const variant = variantEdge.node;

            if (variant.sku === sku) {
              return {
                price: variant.price,
                productTitle: product.title,
                variant: variant,
              };
            }
          }
        }
      }

      return null;
    },
  },
};
</script>

<style scoped src="./fact-finder-item-fi" />
